import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CreateInquiryDto, InquiryService } from '@millionshow/api-client';
import {
  Fieldset,
  FormatInputDirective,
  ModalService,
  ToastService,
} from '@millionshow/client-libs';
import { InquiryCompleteModal } from '../modals/inquiry-complete.modal';
import { SeoService } from '../../../services/seo.service';

@Component({
  selector: 'app-inquiry-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    Fieldset,
    FormatInputDirective,
  ],
  templateUrl: './inquiry-form.page.html',
})
export default class InquiryFormPage {
  private readonly inquiryService = inject(InquiryService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly seoService = inject(SeoService);

  form = new FormGroup({
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    contact: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    title: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    content: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(1000)],
    }),
  });

  constructor() {
    this.seoService.set({
      title: '문의 작성',
      description: '밀리언쇼 홍보 제휴 문의 내용을 작성하고 접수하세요.',
      path: '/inquiry/form',
      robots: 'noindex, follow',
    });
  }

  /**
   * 폼 제출 유효성 검사
   */
  canSubmit(): boolean {
    return this.form.valid;
  }

  /**
   * 제휴 문의 등록 처리
   */
  submitInquiry(): void {
    if (!this.canSubmit()) {
      this.toastService.error('필수 항목을 모두 입력해주세요.');
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const normalizedContact = formValue.contact.replace(/[^0-9]/g, '');

    const body: CreateInquiryDto = {
      name: formValue.name,
      contact: normalizedContact,
      title: formValue.title,
      content: formValue.content,
      sourceType: 'MILLIONSHOW',
    };

    this.inquiryService.inquiryControllerCreateV1({ body }).subscribe({
      next: () => {
        const modal = this.modalService.create(InquiryCompleteModal, {
          componentProps: {
            name: formValue.name,
            contact: formValue.contact,
          },
        });

        modal.closed.subscribe(() => {
          this.router.navigate(['/']);
        });
      },
      error: err => {
        const message =
          err?.error?.message ||
          '문의 등록 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';

        this.toastService.error({ message });
        console.error('Inquiry Submission Error:', err);
      },
    });
  }
}
